import * as LabelPrimitive from "@radix-ui/react-label";
import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useId } from "react";

import { cn } from "@/lib/cn";

export function Label({
  className,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) {
  return (
    <LabelPrimitive.Root
      className={cn("text-sm font-medium text-foreground", className)}
      {...props}
    />
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Rendered inside the field, before the input. Decorative only. */
  leadingIcon?: ReactNode;
  /**
   * Declared for the same reason `Textarea` declares it: `useFieldAnchor` hands
   * its registry callback here, and React 19 passes `ref` as an ordinary prop
   * that `InputHTMLAttributes` does not describe. Without it, a control a
   * validation finding must be able to focus would not compile (#35).
   */
  ref?: Ref<HTMLInputElement>;
}

export function Input({ className, leadingIcon, ...props }: InputProps) {
  return (
    <div className="relative">
      {leadingIcon ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground"
        >
          {leadingIcon}
        </span>
      ) : null}
      <input
        className={cn(
          "h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          "disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground",
          "aria-[invalid=true]:border-destructive",
          leadingIcon && "pl-10",
          className,
        )}
        {...props}
      />
    </div>
  );
}

/**
 * A multi-line text control, for a clinical document rather than a value.
 *
 * The first one in this application: everything before #33 read prompt bodies
 * back through `ReadOnlyField`, so a single-line `Input` was enough. A prompt
 * part is a whole pack file — up to 100,000 characters by contract — and
 * authoring one in a 40px box is not authoring it.
 *
 * `readOnly` rather than `disabled` when content refuses edits: a seeded prompt
 * must stay selectable and copyable (the server's own refusal tells the
 * clinician to copy it into a new asset), and a `disabled` textarea is neither.
 * `aria-disabled` is set alongside so the control is still ANNOUNCED as
 * refusing input, matching what `Button` and `Select` do with the same
 * attribute.
 */
export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /**
   * React 19 passes `ref` as an ordinary prop, so no `forwardRef` is needed —
   * but it is declared explicitly because `useFieldAnchor` hands its registry
   * callback here, and an anchor that silently failed to register would produce
   * a validation finding with no jump target and no error anywhere.
   */
  ref?: Ref<HTMLTextAreaElement>;
};

export function Textarea({ className, readOnly, ...props }: TextareaProps) {
  return (
    <textarea
      readOnly={readOnly}
      aria-disabled={readOnly || undefined}
      className={cn(
        "w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground",
        "placeholder:text-muted-foreground",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        "disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground",
        "read-only:cursor-default read-only:bg-muted read-only:text-muted-foreground",
        "aria-[invalid=true]:border-destructive",
        className,
      )}
      {...props}
    />
  );
}

/**
 * A native select.
 *
 * Deliberately not a Radix listbox. The options here are short, closed
 * vocabularies taken straight from contract enums, and the platform control
 * already gives keyboard operation, typeahead, and the correct behaviour inside
 * a dialog on touch devices — none of which a custom listbox gets for free.
 */
export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  /** As on `Input` and `Textarea`: the field-anchor registry writes here. */
  ref?: Ref<HTMLSelectElement>;
};

export function Select({
  className,
  children,
  ...props
}: SelectProps) {
  return (
    <select
      className={cn(
        "h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        "disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground",
        // The `aria-disabled` half, which `Button` has carried since it chose
        // that attribute over the native one: without it a control announced as
        // disabled still LOOKS fully enabled, so a sighted user sees a normal
        // select beside a visibly greyed submit and the two disagree about
        // whether anything is wrong. `Button.tsx` claims this of "every
        // disabled control in this application"; this makes that true of
        // `Select` too (claude review, round 10).
        "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
        "aria-[invalid=true]:border-destructive",
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
}

/** Label + control + optional description, wired together for screen readers. */
export function Field({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: (ids: { inputId: string; describedBy?: string }) => ReactNode;
}) {
  const inputId = useId();
  const descriptionId = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId}>{label}</Label>
      {children({
        inputId,
        ...(description ? { describedBy: descriptionId } : {}),
      })}
      {description ? (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );
}
